import { Pill } from '~/components/component-library'
import { ChainCapability, getChain, getTier } from '~/constants/chains'

/**
 * Partial-tier networks are listed alongside full ones, so every partial row
 * carries a caveat naming the features that don't work there. This generic
 * fallback covers a chain the server marks partial for a reason outside
 * MISSING_CAPABILITY_LABELS.
 */
const PARTIAL_CAVEAT = 'Some features may be unavailable on this network.'

// Features a partial chain can visibly lack, in display order. `balances` and
// `transact` are deliberately unmapped — a listed chain missing those is not a
// tooltip-sized problem.
const MISSING_CAPABILITY_LABELS = {
  accounting: 'accounting history',
  nfts: 'NFTs',
  swap: 'swapping',
  bridge: 'bridging',
} satisfies Partial<Record<ChainCapability, string>>

const getPartialCaveat = (chainId: number): string => {
  const capabilities = getChain(chainId)?.capabilities
  if (!capabilities) return PARTIAL_CAVEAT

  const missing = Object.keys(MISSING_CAPABILITY_LABELS)
    .map((capability) => capability as keyof typeof MISSING_CAPABILITY_LABELS)
    .filter((capability) => !capabilities[capability])
    .map((capability) => MISSING_CAPABILITY_LABELS[capability])
  if (missing.length === 0) return PARTIAL_CAVEAT

  const featureList =
    missing.length > 1
      ? `${missing.slice(0, -1).join(', ')} or ${missing.at(-1)}`
      : missing[0]
  return `Splits doesn't support ${featureList} on this network.`
}

/** Renders nothing for a full-tier network, so a picker row can pass any chain. */
export const PartialNetworkPill = ({ chainId }: { chainId: number }) => {
  if (getTier(chainId) !== 'partial') return null

  return (
    <Pill
      size="small"
      label="Partial"
      tooltipMessage={getPartialCaveat(chainId)}
    />
  )
}
